import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { ThemeContext, type ThemeContextValue, type ThemePreference, type ResolvedTheme } from './ThemeContext.ts';

const STORAGE_KEY = 'theme';
const VALID_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw !== null && (VALID_PREFERENCES as readonly string[]).includes(raw)) {
      return raw as ThemePreference;
    }
  } catch {
    // localStorage can throw in private-mode Safari. Fall through.
  }
  return 'system';
}

function osPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function subscribeToOsScheme(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', onChange);
  return () => {
    mq.removeEventListener('change', onChange);
  };
}

/**
 * Apply the user preference to the <html> element as a `data-theme` attribute.
 * Token CSS reads this to flip colors. When preference is `system`, no
 * attribute is set — tokens.css falls back to the
 * `@media (prefers-color-scheme: dark)` rule.
 */
function applyToDom(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (pref === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', pref);
  }
}

/**
 * ThemeProvider — wires the theme system end-to-end:
 *   • Reads stored preference on mount; defaults to `system`.
 *   • Applies the preference to <html data-theme> for tokens to consume.
 *   • Listens to `prefers-color-scheme` changes so `system` mode tracks
 *     the OS in real time without requiring a reload.
 *   • Exposes preference + resolved theme + setter via context.
 *
 * Mount once near the root, above any code that needs theme awareness.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  // The OS scheme is an external store; subscribing this way re-renders on every change,
  // so `system` tracks the OS live without mirroring it into state.
  const osDark = useSyncExternalStore(subscribeToOsScheme, osPrefersDark, () => false);
  const resolved: ResolvedTheme = preference === 'system' ? (osDark ? 'dark' : 'light') : preference;

  // Apply preference to DOM on mount + whenever it changes.
  useEffect(() => {
    applyToDom(preference);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      // Ignore localStorage write failures; in-memory state still updates.
    }
    setPreferenceState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
